import React from "react";
import { Button } from "antd";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useGetFormDetailsQuery } from "../../store/api/apiSlice";
import { SurveyHeader } from "../../components/survey/SurveyHeader";
import RetryRewardButton from "../../components/survey/RetryRewardButton";
import { getSurveyTrackingToken } from "../../utils/surveyTracking";
import {
  clearSurveyPayoutState,
  getSurveyPayoutState,
  persistSurveyPayoutState,
} from "../../utils/surveyPayout";

const ThankYou = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const enquiryType = searchParams.get("type");
  const resolvedFormId = formId || searchParams.get("formId");
  const rootFormId = searchParams.get("rootFormId") || resolvedFormId;
  const { data: formData } = useGetFormDetailsQuery(resolvedFormId, {
    skip: !resolvedFormId,
  });

  const panelToken = getSurveyTrackingToken(resolvedFormId, searchParams);
  const locationPayout = location.state?.payout;
  const storedPayout = React.useMemo(
    () => getSurveyPayoutState(resolvedFormId),
    [resolvedFormId],
  );
  const [payoutState, setPayoutState] = React.useState(
    () => locationPayout || storedPayout || null,
  );

  React.useEffect(() => {
    if (locationPayout) {
      setPayoutState(locationPayout);
      persistSurveyPayoutState(resolvedFormId, locationPayout);
    }
  }, [locationPayout, resolvedFormId]);

  const canRetryPayout =
    Boolean(location.state?.canRetryPayout) ||
    Boolean(payoutState?.canRetry) ||
    false;

  const isAdminThankYou = location.pathname.startsWith(
    "/admin/question/thank-you",
  );
  const questionStartPath = rootFormId
    ? `/form-submissions/${rootFormId}${
        enquiryType === "enquiry" ? "?type=enquiry" : ""
      }`
    : "/";
  const restartPath =
    isAdminThankYou && rootFormId
      ? `/admin/question/${rootFormId}/0`
      : questionStartPath;
  const canRestartForm = formData?.data?.restart_form === true;
  const clearStoredSurveyState = (targetFormId) => {
    if (!targetFormId) return;

    try {
      localStorage.removeItem(`survey_${targetFormId}_responses`);
      localStorage.removeItem(`survey_${targetFormId}_responseId`);
      localStorage.removeItem("SurevyUserId");
      localStorage.removeItem("surveyUserData");
      clearSurveyPayoutState(targetFormId);
    } catch {
      // ignore storage access issues
    }
  };
  const nextSurveyLink = React.useMemo(() => {
    const rawNext = formData?.data?.next;
    if (!rawNext || !rootFormId) return rawNext;

    try {
      const parsed = new URL(rawNext, window.location.origin);
      if (!parsed.searchParams.get("rootFormId")) {
        parsed.searchParams.set("rootFormId", rootFormId);
      }
      return parsed.toString();
    } catch {
      return rawNext;
    }
  }, [formData?.data?.next, rootFormId]);

  return (
    <div className="box-border flex h-dvh flex-col p-2 sm:p-3">
      <div className="mx-auto flex min-h-0 w-full flex-1 flex-col">
        {enquiryType !== "enquiry" ? (
          <SurveyHeader
            organization={formData?.data?.organization}
            form={formData?.data}
            onCancel={() =>
              navigate(isAdminThankYou ? "/admin/dashboard" : "/")
            }
          />
        ) : (
          <div className="h-24 md:h-32" />
        )}
        <div className="min-h-0 w-full flex-1">
          <div className="flex min-h-0 flex-col items-center justify-center gap-6 overflow-x-hidden rounded-3xl border bg-gray-50 p-2 text-center max-md:flex-1 max-md:min-h-0 max-md:overflow-y-auto md:h-[calc(100%-35px)] md:overflow-hidden md:p-5 lg:h-[calc(100%-60px)]">
            {formData?.data?.next ? (
              <div className="flex flex-col items-center gap-4 text-center md:gap-[clamp(1rem,2dvh_+_0.5rem,1.75rem)]">
                <h1 className="mx-auto mb-2 max-w-3xl whitespace-normal break-words text-3xl font-semibold leading-snug text-gray-900 md:leading-[1.08] md:text-[clamp(1.75rem,1.1rem_+_4.2dvh,4rem)]">
                  <span className="block">Congratulations</span>
                  <span className="block">you&apos;re 95% there!</span>
                </h1>
                <p className="text-base leading-relaxed text-gray-900 md:text-[clamp(0.9375rem,0.72rem_+_2dvh,1.75rem)]">
                  You&apos;ve reached the final question
                </p>
                <a
                  href={nextSurveyLink}
                  className="inline-flex min-h-[44px] max-w-full items-center justify-center rounded-xl bg-[#18181B] px-5 py-3 text-base font-medium text-white no-underline transition-colors hover:bg-[#2f3b4d] md:px-[clamp(1rem,2dvh_+_0.5rem,1.5rem)] md:py-[clamp(0.5rem,1dvh_+_0.35rem,0.65rem)] md:text-[clamp(0.8125rem,0.65rem_+_1.1dvh,0.9375rem)]"
                >
                  Click here
                </a>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-5 text-center md:gap-[clamp(1rem,2.25dvh_+_0.5rem,2rem)]">
                <h1 className="mx-auto m-0 max-w-3xl whitespace-normal break-words text-3xl font-semibold leading-snug text-gray-900 md:leading-[1.08] md:text-[clamp(1.75rem,1.1rem_+_4.2dvh,4rem)]">
                  Thank You!
                </h1>
                {payoutState?.payoutError && !payoutState?.payoutSent ? (
                  <p className="m-0 max-w-lg whitespace-pre-wrap text-base leading-relaxed text-red-600 md:text-[clamp(0.9375rem,0.72rem_+_1.5dvh,1.25rem)]">
                    {payoutState.payoutError}
                  </p>
                ) : null}
                {payoutState?.payoutSent ? (
                  <p className="m-0 max-w-lg text-base leading-relaxed text-green-700 md:text-[clamp(0.9375rem,0.72rem_+_1.5dvh,1.25rem)]">
                    Reward email sent successfully
                  </p>
                ) : null}
              </div>
            )}
            {canRetryPayout && panelToken && !payoutState?.payoutSent ? (
              <RetryRewardButton
                token={panelToken}
                formId={resolvedFormId}
                onPayoutUpdate={setPayoutState}
                onSuccess={(payout) => {
                  if (payout?.payoutSent) {
                    setPayoutState(payout);
                  }
                }}
              />
            ) : null}
            {canRestartForm && !formData?.data?.next && (
              <Button
                type="primary"
                className="mt-2 h-11 min-h-[44px] w-full max-w-md shrink-0 rounded-2xl border-none bg-[#202835] px-5 text-base font-medium hover:bg-[#2f3b4d] sm:w-auto md:h-[clamp(2.5rem,6dvh_+_1rem,2.75rem)] md:px-[clamp(1rem,2dvh_+_0.5rem,1.5rem)] md:!text-[clamp(0.8125rem,0.65rem_+_1.1dvh,0.9375rem)]"
                onClick={() => {
                  clearStoredSurveyState(rootFormId);
                  navigate(restartPath);
                }}
              >
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;
